import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bell, Mail, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Card, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Select } from '../ui/Select';

/** Server/preferences keys — keep aligned with InvoiceSend fallbacks */
export const ROUTINE_EMAIL_TEMPLATE_KEYS = {
  standard: 'invoice_link_standard',
  reminder1: 'invoice_link_reminder_1',
  reminder2: 'invoice_link_reminder_2',
  reminder3: 'invoice_link_reminder_3',
  statement: 'statement_link',
};

/** Default subject/body per preferences key (same copy as InvoiceSend.jsx literals) */
export const ROUTINE_EMAIL_TEMPLATE_DEFAULTS = {
  [ROUTINE_EMAIL_TEMPLATE_KEYS.standard]: {
    subject: 'New Invoice Created',
    body: `Dear @FirstName@,

To view your invoice or obtain a PDF copy please follow the link below:

@DirectInvoiceLink@

If a certificate was issued for the works carried out it will be attached to your invoice, please let us know if you have any issues in accessing them.`,
  },
  [ROUTINE_EMAIL_TEMPLATE_KEYS.reminder1]: {
    subject: 'Reminder: Invoice outstanding',
    body: `Dear @FirstName@,

Our records show an invoice is still outstanding. Please view and pay using the secure link below:

@DirectInvoiceLink@

If you have already paid, please disregard this message.`,
  },
  [ROUTINE_EMAIL_TEMPLATE_KEYS.reminder2]: {
    subject: 'Second reminder: Invoice outstanding',
    body: `Dear @FirstName@,

We are writing again regarding an unpaid invoice. You can review the document and payment options here:

@DirectInvoiceLink@

Please settle at your earliest convenience, or contact us if there is an issue.`,
  },
  [ROUTINE_EMAIL_TEMPLATE_KEYS.reminder3]: {
    subject: 'Final reminder: Invoice outstanding',
    body: `Dear @FirstName@,

This is a final reminder about an outstanding invoice. View and pay online using the link below:

@DirectInvoiceLink@

If payment has been sent, please accept our thanks and let us know.`,
  },
  [ROUTINE_EMAIL_TEMPLATE_KEYS.statement]: {
    subject: 'Account statement',
    body: `Dear @FirstName@,

Your account statement is available to view online using the secure link below:

@DirectStatementLink@

If you have any questions, please contact us.

@EmailSignature@`,
  },
};

const EDITABLE_ROWS = [
  {
    key: ROUTINE_EMAIL_TEMPLATE_KEYS.standard,
    title: 'Invoice link (standard)',
    description: 'Used when sending an invoice link from the Send Invoice screen.',
  },
  {
    key: ROUTINE_EMAIL_TEMPLATE_KEYS.reminder1,
    title: 'Invoice reminder 1',
    description: 'First reminder template for outstanding invoices.',
  },
  {
    key: ROUTINE_EMAIL_TEMPLATE_KEYS.reminder2,
    title: 'Invoice reminder 2',
    description: 'Second reminder template.',
  },
  {
    key: ROUTINE_EMAIL_TEMPLATE_KEYS.reminder3,
    title: 'Invoice reminder 3',
    description: 'Final reminder template.',
  },
  {
    key: ROUTINE_EMAIL_TEMPLATE_KEYS.statement,
    title: 'Account statement',
    description: 'Used when emailing a statement link from the client Statement page.',
  },
];

const COMING_SOON_ROWS = [
  { key: 'recurring_invoice', title: 'Recurring invoice', description: 'Automated emails for recurring billing.' },
  { key: 'credit_note', title: 'Credit note', description: 'Credit note notifications.' },
  { key: 'payment_received', title: 'Payment received', description: 'Thank-you / receipt emails.' },
  { key: 'remittance', title: 'Remittance advice', description: 'Supplier remittance messages.' },
  { key: 'direct_debit', title: 'Direct Debit', description: 'DD confirmation and notices.' },
  { key: 'letters', title: 'Letters', description: 'Formal letter templates.' },
];

const ROUTINE_EMAIL_TOKENS = [
  { token: '@FirstName@', description: 'Recipient first name' },
  { token: '@DirectInvoiceLink@', description: 'Secure link to the invoice' },
  { token: '@DirectStatementLink@', description: 'Secure link to the account statement' },
  { token: '@EmailSignature@', description: 'Your saved signature (below)' },
];

const INPUT_CLASS =
  'h-8 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100';
const TEXTAREA_CLASS =
  'min-h-[160px] resize-y font-mono text-xs border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100';

/** Aligned with server `tenantNotificationPreferences.js` */
const DEFAULT_INVOICE_SENT_OFFSETS = [7, 14, 21];
const MAX_INVOICE_SENT_OFFSETS = 5;
const MIN_REMINDER_DAY = 1;
const MAX_REMINDER_DAY = 90;

/**
 * @param {unknown} settings — full settings object from GET /api/settings
 * @returns {{ enabled: boolean, offsets: number[] }}
 */
function parseInvoiceSentRemindersFromSettings(settings) {
  const branch = settings?.preferences?.notifications?.invoice_sent_reminders;
  const enabled = branch?.enabled === true;
  const raw = branch?.days_after_sent;
  if (!Array.isArray(raw) || raw.length === 0) {
    return { enabled, offsets: [...DEFAULT_INVOICE_SENT_OFFSETS] };
  }
  const seen = new Set();
  for (const v of raw) {
    const n = typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : Number.NaN;
    if (!Number.isFinite(n)) continue;
    seen.add(Math.min(MAX_REMINDER_DAY, Math.max(MIN_REMINDER_DAY, n)));
  }
  if (seen.size === 0) {
    return { enabled, offsets: [...DEFAULT_INVOICE_SENT_OFFSETS] };
  }
  const offsets = Array.from(seen)
    .sort((a, b) => a - b)
    .slice(0, MAX_INVOICE_SENT_OFFSETS);
  return { enabled, offsets };
}

/**
 * @param {number[]} offsets
 * @returns {number[]}
 */
function normalizeInvoiceSentOffsetsForSave(offsets) {
  const seen = new Set();
  for (const v of offsets) {
    const n = typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : Number.NaN;
    if (!Number.isFinite(n)) continue;
    seen.add(Math.min(MAX_REMINDER_DAY, Math.max(MIN_REMINDER_DAY, n)));
  }
  if (seen.size === 0) return [...DEFAULT_INVOICE_SENT_OFFSETS];
  return Array.from(seen)
    .sort((a, b) => a - b)
    .slice(0, MAX_INVOICE_SENT_OFFSETS);
}

function emptyTemplatesState() {
  const templates = {};
  for (const row of EDITABLE_ROWS) {
    const d = ROUTINE_EMAIL_TEMPLATE_DEFAULTS[row.key];
    templates[row.key] = { subject: d.subject, body: d.body };
  }
  return templates;
}

function buildStateFromPreferences(saved) {
  const templates = emptyTemplatesState();
  if (saved?.templates) {
    for (const row of EDITABLE_ROWS) {
      const t = saved.templates[row.key];
      if (t && typeof t === 'object') {
        templates[row.key] = {
          subject: t.subject != null ? String(t.subject) : templates[row.key].subject,
          body: t.body != null ? String(t.body) : templates[row.key].body,
        };
      }
    }
  }
  return {
    identity: {
      defaultFromEmail: saved?.identity?.defaultFromEmail != null ? String(saved.identity.defaultFromEmail) : '',
      fromName: saved?.identity?.fromName != null ? String(saved.identity.fromName) : '',
      signatureHtml:
        saved?.identity?.signatureHtml != null ? String(saved.identity.signatureHtml) : '',
    },
    templates,
  };
}

/**
 * Routine email templates + sender identity for Send Invoice flow.
 * Persists under settings.preferences.routineEmails and preferences.notifications.invoice_sent_reminders
 * via PUT /api/settings (notifications are shallow-merged server-side).
 * @param {string | null} [expandFromQuery] — template row key from `?expand=` (Settings passes searchParams.get('expand')).
 */
export default function RoutineEmailsSettings({
  token,
  settings,
  setSettings,
  invoiceSettings,
  setSaving,
  setError,
  setSuccess,
  saving,
  expandFromQuery,
}) {
  const savedSlice = settings?.preferences?.routineEmails;

  const [identity, setIdentity] = useState(() => buildStateFromPreferences(savedSlice).identity);
  const [templates, setTemplates] = useState(() => buildStateFromPreferences(savedSlice).templates);
  const [openKey, setOpenKey] = useState(null);
  const [tokenPick, setTokenPick] = useState(ROUTINE_EMAIL_TOKENS[0].token);
  const [insertField, setInsertField] = useState('body');

  const [invoiceSentRemindersEnabled, setInvoiceSentRemindersEnabled] = useState(false);
  const [invoiceSentOffsets, setInvoiceSentOffsets] = useState(() => [...DEFAULT_INVOICE_SENT_OFFSETS]);

  const subjectRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    const next = buildStateFromPreferences(settings?.preferences?.routineEmails);
    setIdentity(next.identity);
    setTemplates(next.templates);
  }, [settings?.preferences?.routineEmails]);

  useEffect(() => {
    const parsed = parseInvoiceSentRemindersFromSettings(settings);
    setInvoiceSentRemindersEnabled(parsed.enabled);
    setInvoiceSentOffsets(parsed.offsets);
  }, [settings]);

  useEffect(() => {
    if (expandFromQuery && EDITABLE_ROWS.some((r) => r.key === expandFromQuery)) {
      setOpenKey(expandFromQuery);
    }
  }, [expandFromQuery]);

  const routineEmailsPayload = useMemo(
    () => ({
      identity: {
        defaultFromEmail: identity.defaultFromEmail.trim(),
        fromName: identity.fromName.trim(),
        signatureHtml: identity.signatureHtml,
      },
      templates,
    }),
    [identity, templates]
  );

  const updateIdentity = useCallback((field) => (e) => {
    const v = e.target.value;
    setIdentity((prev) => ({ ...prev, [field]: v }));
  }, []);

  const updateTemplate = useCallback((key, field) => (e) => {
    const v = e.target.value;
    setTemplates((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: v },
    }));
  }, []);

  const revertTemplate = useCallback((key) => {
    const d = ROUTINE_EMAIL_TEMPLATE_DEFAULTS[key];
    if (!d) return;
    setTemplates((prev) => ({
      ...prev,
      [key]: { subject: d.subject, body: d.body },
    }));
  }, []);

  const toggleOpen = useCallback((key) => {
    setOpenKey((prev) => (prev === key ? null : key));
  }, []);

  const handleInsertToken = useCallback(() => {
    if (!openKey) return;
    const tokenStr = tokenPick;
    const el = insertField === 'subject' ? subjectRef.current : bodyRef.current;
    const field = insertField === 'subject' ? 'subject' : 'body';
    if (el) {
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      const v = el.value;
      const next = v.slice(0, start) + tokenStr + v.slice(end);
      setTemplates((prev) => ({
        ...prev,
        [openKey]: { ...prev[openKey], [field]: next },
      }));
      requestAnimationFrame(() => {
        try {
          el.focus();
          const pos = start + tokenStr.length;
          el.setSelectionRange(pos, pos);
        } catch {
          /* ignore */
        }
      });
    } else {
      setTemplates((prev) => ({
        ...prev,
        [openKey]: {
          ...prev[openKey],
          [field]: (prev[openKey][field] || '') + tokenStr,
        },
      }));
    }
  }, [openKey, tokenPick, insertField]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess('');
    try {
      const notificationsBase =
        settings?.preferences?.notifications && typeof settings.preferences.notifications === 'object'
          ? settings.preferences.notifications
          : {};
      const daysAfterSent = normalizeInvoiceSentOffsetsForSave(invoiceSentOffsets);

      const settingsToSave = {
        ...settings,
        preferences: {
          ...(settings.preferences || {}),
          invoice: invoiceSettings,
          routineEmails: routineEmailsPayload,
          notifications: {
            ...notificationsBase,
            invoice_sent_reminders: {
              enabled: invoiceSentRemindersEnabled,
              days_after_sent: daysAfterSent,
            },
          },
        },
      };

      const res = await fetch('/api/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(settingsToSave),
      });

      if (!res.ok) throw new Error('Failed to save routine email settings');
      const data = await res.json();

      if (data.settings?.preferences) {
        setSettings((prev) => ({ ...prev, preferences: data.settings.preferences }));
      }

      setSuccess('Routine email and reminder settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save routine email settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="mb-6">
          <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">Routine emails</h2>
          <p className="text-xs text-[var(--ds-text-secondary)] mt-1">
            Default sender details and templates for the Send Invoice screen (standard and reminders) and account
            statement emails. For the rich HTML email used when sending from the invoice editor without a custom
            message, use{' '}
            <span className="text-[var(--ds-text-primary)]">Invoice Settings → Client email</span>.
          </p>
        </div>

        <form onSubmit={handleSave} className="space-y-8">
          <section className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Global</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Default from email
                </label>
                <Input
                  type="email"
                  value={identity.defaultFromEmail}
                  onChange={updateIdentity('defaultFromEmail')}
                  placeholder="Leave blank to use system default"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">From name</label>
                <Input
                  type="text"
                  value={identity.fromName}
                  onChange={updateIdentity('fromName')}
                  placeholder="e.g. Your trading name"
                  className={INPUT_CLASS}
                />
              </div>
            </div>
            <div>
              <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email signature
              </label>
              <Textarea
                value={identity.signatureHtml}
                onChange={updateIdentity('signatureHtml')}
                placeholder="Plain text or simple HTML; inserted when you use @EmailSignature@ in a template."
                className={`min-h-[100px] resize-y ${TEXTAREA_CLASS}`}
              />
              <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
                Shown only where you place <code className="text-gray-800 dark:text-gray-200">@EmailSignature@</code> in
                the message body.
              </p>
            </div>
          </section>

          <section className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 space-y-4">
            <div className="flex items-start gap-3">
              <Bell className="h-5 w-5 shrink-0 text-[var(--ds-text-muted)] mt-0.5" aria-hidden />
              <div className="flex-1 min-w-0 space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Automated invoice reminders (after send)
                  </h3>
                  <p className="text-micro text-[var(--ds-text-muted)] mt-1">
                    Sends reminder emails on the days you choose <span className="text-gray-800 dark:text-gray-200">after</span>{' '}
                    the invoice issue date, for invoices still in Sent, Part-paid, or Overdue. This is separate from the
                    optional{' '}
                    <span className="font-medium text-gray-800 dark:text-gray-200">before due date</span>{' '}
                    payment reminder scheduled when you email an invoice. Clients can opt out per contact; delivery uses
                    your notification send window when configured.
                  </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={invoiceSentRemindersEnabled}
                    onChange={(e) => setInvoiceSentRemindersEnabled(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 bg-white dark:bg-gray-700"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Enable post-send reminders for this business</span>
                </label>
                <div className="space-y-2">
                  <span className="block text-meta font-medium text-gray-700 dark:text-gray-300">
                    Days after issue date (1–90 each, up to {MAX_INVOICE_SENT_OFFSETS} dates; duplicates are merged on save)
                  </span>
                  <ul className="space-y-2">
                    {invoiceSentOffsets.map((day, idx) => (
                      <li key={idx} className="flex flex-wrap items-center gap-2">
                        <label className="text-micro text-gray-600 dark:text-gray-400 w-28 shrink-0">
                          Reminder {idx + 1}
                        </label>
                        <Input
                          type="number"
                          min={MIN_REMINDER_DAY}
                          max={MAX_REMINDER_DAY}
                          value={day}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            setInvoiceSentOffsets((prev) => {
                              const next = [...prev];
                              next[idx] = Number.isFinite(v) ? v : prev[idx];
                              return next;
                            });
                          }}
                          className={`w-24 ${INPUT_CLASS}`}
                          disabled={!invoiceSentRemindersEnabled}
                        />
                        <span className="text-micro text-[var(--ds-text-muted)]">days after issued</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {invoiceSentOffsets.length < MAX_INVOICE_SENT_OFFSETS && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!invoiceSentRemindersEnabled}
                        onClick={() =>
                          setInvoiceSentOffsets((prev) => {
                            if (prev.length >= MAX_INVOICE_SENT_OFFSETS) return prev;
                            const last = prev[prev.length - 1] ?? 7;
                            const nextDay = Math.min(MAX_REMINDER_DAY, Math.max(MIN_REMINDER_DAY, last + 7));
                            return [...prev, nextDay];
                          })
                        }
                      >
                        Add another day
                      </Button>
                    )}
                    {invoiceSentOffsets.length > 3 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={!invoiceSentRemindersEnabled}
                        onClick={() => setInvoiceSentOffsets((prev) => (prev.length > 3 ? prev.slice(0, -1) : prev))}
                      >
                        Remove last
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Templates</h3>
            <ul className="space-y-2">
              {EDITABLE_ROWS.map((row) => {
                const isOpen = openKey === row.key;
                const t = templates[row.key] || ROUTINE_EMAIL_TEMPLATE_DEFAULTS[row.key];
                return (
                  <li
                    key={row.key}
                    className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 overflow-hidden"
                  >
                    <div className="flex items-center gap-3 px-4 py-3">
                      <Mail className="h-5 w-5 shrink-0 text-[var(--ds-text-muted)]" aria-hidden />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 dark:text-gray-100">{row.title}</div>
                        <p className="text-micro text-[var(--ds-text-muted)]">{row.description}</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => toggleOpen(row.key)}
                        className="shrink-0"
                      >
                        {isOpen ? (
                          <>
                            <ChevronUp className="h-4 w-4 mr-1" />
                            Hide
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-4 w-4 mr-1" />
                            Modify
                          </>
                        )}
                      </Button>
                    </div>
                    {isOpen && (
                      <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-4 space-y-4 bg-gray-50 dark:bg-gray-900/40">
                        <div className="rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-3 flex flex-wrap items-end gap-2">
                          <div className="flex-1 min-w-[140px]">
                            <label className="block text-micro font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Token
                            </label>
                            <Select
                              value={tokenPick}
                              onChange={(e) => setTokenPick(e.target.value)}
                              className={`w-full text-xs ${INPUT_CLASS}`}
                            >
                              {ROUTINE_EMAIL_TOKENS.map(({ token: tok, description }) => (
                                <option key={tok} value={tok}>
                                  {tok} — {description}
                                </option>
                              ))}
                            </Select>
                          </div>
                          <div className="w-full sm:w-auto min-w-[120px]">
                            <label className="block text-micro font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Insert into
                            </label>
                            <Select
                              value={insertField}
                              onChange={(e) => setInsertField(e.target.value)}
                              className={`w-full text-xs ${INPUT_CLASS}`}
                            >
                              <option value="subject">Subject</option>
                              <option value="body">Message body</option>
                            </Select>
                          </div>
                          <Button type="button" variant="secondary" size="sm" onClick={handleInsertToken}>
                            Insert token
                          </Button>
                        </div>

                        <div>
                          <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Subject
                          </label>
                          <Input
                            ref={subjectRef}
                            type="text"
                            value={t.subject}
                            onChange={updateTemplate(row.key, 'subject')}
                            className={INPUT_CLASS}
                          />
                        </div>
                        <div>
                          <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Message body
                          </label>
                          <Textarea
                            ref={bodyRef}
                            value={t.body}
                            onChange={updateTemplate(row.key, 'body')}
                            className={TEXTAREA_CLASS}
                          />
                        </div>
                        <div>
                          <Button type="button" variant="ghost" size="sm" onClick={() => revertTemplate(row.key)}>
                            Revert to default
                          </Button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}

              {COMING_SOON_ROWS.map((row) => (
                <li
                  key={row.key}
                  className="rounded-md border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/20 px-4 py-3 flex items-center gap-3 opacity-75"
                >
                  <Mail className="h-5 w-5 shrink-0 text-[var(--ds-text-muted)]" aria-hidden />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-700 dark:text-gray-300">{row.title}</span>
                      <Badge variant="secondary" className="text-micro">
                        Coming soon
                      </Badge>
                    </div>
                    <p className="text-micro text-[var(--ds-text-muted)]">{row.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <div className="pt-2">
            <Button variant="outline" size="sm" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save routine emails & reminders'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
